/**
 * The workshops block on a region's page (BON-534).
 *
 * Covers the region AND everything beneath it — the server scopes to the whole
 * subtree, so a parent shows its children's workshops without the page asking
 * for them separately.
 *
 * Tablet and desktop get the month calendar. A phone defaults to the design's
 * mobile presentation instead — sub-region filter chips over the shared
 * `WorkshopList` — and can switch to a calendar when the month at a glance
 * matters more than room to read.
 *
 * The calendar's List view shows the pacing flag. Overview is reachable only in
 * admin mode; teachers are never shown pacing. The viewer's own workshops are
 * marked in the List only: in the grid, the "mine" highlight would replace the
 * sub-region colour the legend filters by.
 *
 * No create action anywhere — Overview is read-only, and omitting the callback
 * is how the calendar knows not to offer one. Workshops are still openable:
 * reading a workshop is not editing it, and a calendar you cannot click is a
 * dead end.
 *
 * Every layout reads the one set of choices held here and kept in the address —
 * view, date, hidden sub-regions, cancelled shown — so switching between phone
 * and desktop, or between the phone's List and Calendar, keeps the filters.
 */
import { useSearchParamState } from '../../../../hooks/use-search-param-state';
import { SegmentedControl } from '../../primitives/SegmentedControl';
import { useCalendarState } from '../../primitives/use-calendar-state';

import { calendarParams, overviewCalendarViews } from './overview-calendar-params';
import { useOverviewViewport } from './overview-viewport';
import { RegionWorkshopsAhead } from './RegionWorkshopsAhead';
import { RegionWorkshopsCalendar } from './RegionWorkshopsCalendar';
import { RegionWorkshopsPhoneCalendar } from './RegionWorkshopsPhoneCalendar';
import { usePhoneWorkshopsLayout, type PhoneWorkshopsLayout } from './use-phone-workshops-layout';

export interface RegionWorkshopsProps {
  /** The region's full ancestor path. */
  path: string[];
}

const LAYOUT_OPTIONS: ReadonlyArray<{ value: PhoneWorkshopsLayout; label: string }> = [
  { value: 'list', label: 'List' },
  { value: 'calendar', label: 'Calendar' },
];

export function RegionWorkshops({ path }: RegionWorkshopsProps) {
  const { isPhone } = useOverviewViewport();
  const [layout, setLayout] = usePhoneWorkshopsLayout();
  const views = overviewCalendarViews(isPhone);
  const choices = useSearchParamState(calendarParams(views));
  const calendar = useCalendarState(choices);

  if (!isPhone) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <RegionWorkshopsCalendar path={path} calendar={calendar} views={views} />
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ alignSelf: 'flex-end' }}>
        <SegmentedControl
          value={layout}
          onChange={setLayout}
          options={LAYOUT_OPTIONS}
          ariaLabel="Show workshops as"
        />
      </div>
      {layout === 'calendar' ? (
        <RegionWorkshopsPhoneCalendar path={path} calendar={calendar} views={views} />
      ) : (
        <RegionWorkshopsAhead path={path} calendar={calendar} />
      )}
    </section>
  );
}
